import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Modal, Stack, TextField, createFilterOptions } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import { BrandOption, RawProductBrand, RawProductBrandPatch } from 'services/rawProductService';

interface Props {
  open: boolean;
  productName: string;
  // The brand the pack is filed under now, or null.
  current: RawProductBrand | null;
  brands: BrandOption[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (patch: RawProductBrandPatch) => void;
}

// Searched on every spelling a brand has been seen as, not only its two names:
// a reviewer types what the pack in front of them prints.
const filterBrands = createFilterOptions<BrandOption>({
  stringify: (brand) => [brand.nameEn, brand.nameAr, ...brand.aliases].filter(Boolean).join(' '),
});

export default function BrandModal({
  open,
  productName,
  current,
  brands,
  isSaving,
  onClose,
  onSave,
}: Props) {
  const [selected, setSelected] = useState<BrandOption | null>(null);
  const [en, setEn] = useState('');
  const [ar, setAr] = useState('');

  // The list is fetched once a session, so a brand this pack's own reading just
  // created is not in it yet. The pack's brand is always an option, whether the
  // list has caught up or not - otherwise the editor opens empty on exactly the
  // brand somebody wants to correct.
  const options = useMemo<BrandOption[]>(() => {
    if (!current || brands.some((brand) => brand.id === current.id)) return brands;
    return [{ ...current, productCount: 0 }, ...brands];
  }, [brands, current]);

  // Opens on what the row holds, so a reviewer sees what they are changing
  // rather than an empty box.
  const openOn = current?.id ?? null;
  useEffect(() => {
    if (!open) return;
    const brand = options.find((option) => option.id === openOn) ?? null;
    setSelected(brand);
    setEn(brand?.nameEn ?? '');
    setAr(brand?.nameAr ?? '');
  }, [open, openOn, options]);

  const pick = (brand: BrandOption | null) => {
    setSelected(brand);
    setEn(brand?.nameEn ?? '');
    setAr(brand?.nameAr ?? '');
  };

  // Only a name that was actually changed is sent: an unchanged one sent back
  // would be stored as a spelling a reviewer vouched for.
  const enChanged = en.trim() !== (selected?.nameEn ?? '');
  const arChanged = ar.trim() !== (selected?.nameAr ?? '');
  const changed = (selected?.id ?? null) !== openOn || enChanged || arChanged;

  const note = selected
    ? enChanged || arChanged
      ? `Renames this brand on all ${selected.productCount} pack${selected.productCount === 1 ? '' : 's'} filed under it.`
      : 'Files this pack under the brand chosen.'
    : en.trim() || ar.trim()
      ? 'Makes a new brand with these names for this pack.'
      : 'Says this pack names no brand.';

  return (
    <Modal open={open} onClose={isSaving ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          width: 560,
          maxWidth: '92vw',
        }}
      >
        <CustomText text="Edit brand" fontWeight={700} fontSize={18} />
        <Box mt={0.5}>
          <CustomText text={productName} fontSize={13} color={colors.gray} />
        </Box>

        <Box mt={2}>
          <Autocomplete
            options={options}
            value={selected}
            disabled={isSaving}
            onChange={(_event, value) => pick(value)}
            filterOptions={filterBrands}
            getOptionLabel={(brand) =>
              `${[brand.nameEn, brand.nameAr].filter(Boolean).join(' / ')} (${brand.productCount})`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Brand" placeholder="Clear for a new brand" autoFocus />
            )}
          />
        </Box>

        <Box
          mt={2}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 2,
            rowGap: 2,
          }}
        >
          <TextField
            size="small"
            label="EN"
            value={en}
            disabled={isSaving}
            onChange={(event) => setEn(event.target.value)}
          />
          <TextField
            size="small"
            label="AR"
            value={ar}
            disabled={isSaving}
            onChange={(event) => setAr(event.target.value)}
            inputProps={{ dir: 'rtl' }}
          />
        </Box>

        <Box mt={1}>
          <CustomText text={note} fontSize={11} color={colors.gray} />
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            height={40}
            disabled={isSaving}
            onClick={onClose}
          />
          <CustomButton
            text="Save"
            height={40}
            isLoading={isSaving}
            disabled={!changed}
            onClick={() =>
              onSave({
                brandId: selected?.id ?? null,
                ...(!selected || enChanged ? { nameEn: en.trim() || null } : {}),
                ...(!selected || arChanged ? { nameAr: ar.trim() || null } : {}),
              })
            }
          />
        </Stack>
      </Box>
    </Modal>
  );
}
